import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, RefreshCw } from 'lucide-react';
import { getRejudgeStatus, rejudgeArticle } from '../lib/api/qg-actions.js';

interface RejudgeActionProps {
  articleId: string;
  /** Refetch the article once the re-judge has finished. */
  onDone?: () => void;
}

const TERMINAL = new Set(['completed', 'failed']);

/** Re-judge a held filing after an edit (ruling 33).
 *
 *  Queues a durable re-gate job — the whole gate re-runs on the edited text,
 *  and only a clean verdict releases, attributed to the operator. Polls the
 *  JOB rather than the article: a skipped or failed job never changes the
 *  verdict, and polling the article would spin forever. */
export function RejudgeAction({ articleId, onDone }: RejudgeActionProps) {
  const [jobId, setJobId] = useState<string | null>(null);
  const queue = useMutation({
    mutationFn: () => rejudgeArticle(articleId),
    onSuccess: (d) => setJobId(d.job_id),
  });
  const status = useQuery({
    queryKey: ['rejudge-status', articleId, jobId],
    queryFn: () => getRejudgeStatus(articleId, jobId!),
    enabled: !!jobId,
    refetchInterval: (q) => (q.state.data && TERMINAL.has(q.state.data.status) ? false : 5000),
  });
  const s = status.data;
  const done = !!s && TERMINAL.has(s.status);
  // Refetch the article once, when the job reaches a terminal state.
  useEffect(() => {
    if (done) onDone?.();
  }, [done]); // eslint-disable-line react-hooks/exhaustive-deps

  let outcome: string | null = null;
  if (s?.status === 'failed') outcome = `Re-judge failed: ${s.error ?? 'unknown error'}`;
  else if (s?.status === 'completed' && s.result?.skipped) outcome = 'Nothing to re-judge — the article is no longer held.';
  else if (s?.status === 'completed' && s.result) {
    outcome = s.result.released
      ? `Re-judged ${s.result.old_verdict} → ${s.result.new_verdict}: released${s.result.publish?.queued === false ? ` (not published: ${s.result.publish.reason})` : ' and publishing'}.`
      : `Re-judged: still ${s.result.new_verdict}. The remaining findings below are current.`;
  }

  return (
    <div className="rounded-md border border-primary-200 bg-primary-50 p-2 text-xs flex items-center gap-2 flex-wrap">
      {!jobId ? (
        <>
          <button
            type="button"
            disabled={queue.isPending}
            onClick={() => queue.mutate()}
            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-surface text-primary-700 border border-primary-300 font-semibold hover:bg-primary-100 disabled:opacity-50"
            title="Re-run the whole quality gate on the edited text (one model call, about a minute). Only a clean verdict releases and publishes, recorded as you."
          >
            <RefreshCw className="h-3.5 w-3.5" /> Re-judge after your edit
          </button>
          <span className="text-primary-800">For findings that need judgement — after editing, let the gate check again.</span>
          {queue.isError && <span className="text-danger-700">{(queue.error as Error).message}</span>}
        </>
      ) : outcome ? (
        <span className={s?.status === 'failed' ? 'text-danger-700' : 'text-primary-800'}>{outcome}</span>
      ) : (
        <span className="text-primary-800 inline-flex items-center gap-1">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Re-judging{s?.status === 'pending' ? ' (queued)' : ''}…
        </span>
      )}
    </div>
  );
}
